import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { ButtonSpinner } from "./Loading";

type Props = {
  projectName: string | null;
  // The current page's share token, threaded through so signing up here
  // grants the visitor durable access to *this* project even if they had
  // no prior invite or comment history.
  shareToken: string;
  // Incrementing token from the parent — when this changes the header
  // auto-opens the sign-up panel (used after the visitor posts their
  // first comment as a nudge to sign up and track their feedback).
  pulseToken?: number;
};

// Minimal chrome for the public /p/:token surface. Deliberately does NOT
// read useAuth() / useClientAuth() so a stale owner session in the same
// browser can never leak an owner identity into the public link.
export function ReviewerHeader({ projectName, shareToken, pulseToken }: Props) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Parent flips pulseToken to ask the header to surface the sign-up
  // panel. Skip the initial undefined→undefined render.
  useEffect(() => {
    if (pulseToken === undefined) return;
    setOpen(true);
  }, [pulseToken]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg(null);
    try {
      await api.clientLogin(email.trim().toLowerCase(), shareToken);
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err instanceof ApiError && err.status === 400
          ? "Please enter a valid email."
          : "Couldn't send the link. Try again in a moment.",
      );
    }
  }

  return (
    <header className="border-b border-neutral-200 bg-white">
      <div className="flex h-14 w-full items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-3" aria-label="Clarity by Avista — home">
          <img
            src="/clarity_logo.svg"
            alt="Clarity by Avista"
            className="h-8 w-auto"
            width={192}
            height={62}
          />
          {projectName ? (
            <span className="hidden text-sm text-neutral-500 sm:inline">
              · <span className="text-neutral-700">{projectName}</span>
            </span>
          ) : null}
        </Link>
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setOpen((v) => !v);
              if (status === "sent") setStatus("idle");
            }}
            className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-100"
          >
            Sign in / Sign up
          </button>
          {open ? (
            <div className="absolute right-0 z-20 mt-2 w-80 rounded-lg border border-neutral-200 bg-white p-4 shadow-lg">
              {status === "sent" ? (
                <div className="text-sm">
                  <p className="font-medium text-neutral-900">Check your inbox</p>
                  <p className="mt-1 text-neutral-600">
                    We sent a sign-in link to <span className="font-medium">{email}</span>.
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-neutral-900">
                      Track your feedback
                    </p>
                    <p className="mt-1 text-xs text-neutral-600">
                      Get a one-time sign-in link by email. No password needed.
                    </p>
                  </div>
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none"
                  />
                  {errorMsg ? (
                    <p className="text-xs text-red-600">{errorMsg}</p>
                  ) : null}
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="inline-flex w-full items-center justify-center rounded-md bg-neutral-900 px-3 py-2 text-sm text-white hover:bg-neutral-700 disabled:opacity-50"
                  >
                    {status === "sending" ? (
                      <ButtonSpinner label="Sending" />
                    ) : (
                      "Email me a sign-in link"
                    )}
                  </button>
                </form>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
